import { useEffect, useState } from "react";
import { CheckCircle2, ExternalLink, Link2, LoaderCircle, ShieldAlert, Trash2, Users } from "lucide-react";

type ConnectionStatus = {
  encryptionReady: boolean;
  configured: boolean;
  url: string;
  usernameHint: string;
  lastConnectedAt: string | null;
  lastError: string | null;
};

async function api(path: string, options?: RequestInit) {
  const response = await fetch(path, { credentials: "include", headers: { "Content-Type": "application/json", ...(options?.headers ?? {}) }, ...options });
  const body = await response.json();
  if (!response.ok) throw new Error(body.error ?? "Une erreur est survenue");
  return body;
}

type LocalClass = { id: string; name: string };
type PronoteRoster = { name: string; students: string[] };

export function PronoteView({ localClasses }: { localClasses: LocalClass[] }) {
  const [status, setStatus] = useState<ConnectionStatus | null>(null);
  const [url, setUrl] = useState("https://e216000i.index-education.net/pronote/professeur.html");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [classes, setClasses] = useState<string[]>([]);
  const [rosters, setRosters] = useState<PronoteRoster[]>([]);
  const [rosterWarning, setRosterWarning] = useState("");

  async function load() {
    try {
      const next = await api("/api/admin/pronote");
      setStatus(next);
      setUrl(next.url);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Chargement impossible"); }
  }
  useEffect(() => { void load(); }, []);

  async function test(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true); setError(""); setClasses([]); setRosters([]); setRosterWarning("");
    try {
      const result = await api("/api/admin/pronote/test", { method: "POST", body: JSON.stringify({ url, username, password, pin, localClassNames: localClasses.map((item) => item.name) }) });
      setClasses(result.classes ?? []);
      setRosters(result.rosters ?? []);
      setRosterWarning(result.rosterWarning ?? "");
      setUsername(""); setPassword(""); setPin("");
      await load();
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Connexion Pronote impossible"); }
    finally { setBusy(false); }
  }

  async function disconnect() {
    if (!window.confirm("Supprimer les identifiants Pronote enregistrés ?")) return;
    await api("/api/admin/pronote", { method: "DELETE" });
    setClasses([]); setRosters([]); setRosterWarning(""); setUsername(""); setPassword("");
    await load();
  }

  return <main className="management-page pronote-page">
    <div className="management-heading"><div><span className="eyebrow">Connecteur personnel expérimental</span><h1>Connexion Pronote</h1></div>{status?.configured && <span className="pronote-connected"><CheckCircle2 size={18} /> Connecté</span>}</div>
    <section className="pronote-warning"><ShieldAlert size={22} /><div><strong>Réservé à ton compte administrateur</strong><span>Le connecteur automatise l’interface Pronote. Une mise à jour de Pronote peut nécessiter une réparation.</span></div></section>
    {status && !status.encryptionReady && <p className="account-error">Ajoute d’abord une variable Coolify <strong>PRONOTE_CREDENTIALS_KEY</strong> contenant au moins 32 caractères, puis redéploie.</p>}
    {error && <p className="account-error">{error}</p>}
    <form className="management-form pronote-form" onSubmit={test}>
      <label><span>Adresse de l’Espace Professeurs</span><input type="url" value={url} onChange={(event) => setUrl(event.target.value)} required /></label>
      <label><span>Identifiant Pronote</span><input autoComplete="username" value={username} placeholder={status?.configured ? status.usernameHint : "Identifiant"} onChange={(event) => setUsername(event.target.value)} required={!status?.configured} /></label>
      <label><span>Mot de passe Pronote</span><input type="password" autoComplete="current-password" value={password} placeholder={status?.configured ? "Laisser vide pour réutiliser celui enregistré" : "Mot de passe"} onChange={(event) => setPassword(event.target.value)} required={!status?.configured} /></label>
      <label><span>Code PIN temporaire</span><input type="password" inputMode="numeric" pattern="[0-9]{4,8}" minLength={4} maxLength={8} autoComplete="one-time-code" value={pin} placeholder="Uniquement si Pronote le demande" onChange={(event) => setPin(event.target.value.replace(/\D/g, ""))} /><small>Utilisé une seule fois pour enregistrer « CompetenceO serveur » comme appareil de confiance. Ce code n’est jamais conservé.</small></label>
      <div className="pronote-actions"><button className="primary" disabled={busy || !status?.encryptionReady}>{busy ? <LoaderCircle className="spin" size={17} /> : <Link2 size={17} />}{busy ? "Test en cours…" : status?.configured ? "Retester la connexion" : "Tester et enregistrer"}</button><a className="secondary" href={url} target="_blank" rel="noreferrer"><ExternalLink size={16} /> Ouvrir Pronote</a>{status?.configured && <button type="button" className="pronote-disconnect" onClick={disconnect}><Trash2 size={16} /> Déconnecter</button>}</div>
    </form>
    {status?.lastConnectedAt && <section className="pronote-result"><CheckCircle2 size={21} /><div><strong>Dernier test réussi</strong><span>{new Date(status.lastConnectedAt).toLocaleString("fr-FR")}</span></div></section>}
    {rosterWarning && <p className="account-error">{rosterWarning}</p>}
    {classes.length > 0 && <section className="pronote-classes"><h2>Classes détectées dans Pronote</h2><div className="pronote-class-chips">{classes.map((item) => <span key={item}>{item}</span>)}</div>{rosters.length === 0 && <p>Aucun nom de classe ne correspond encore exactement entre CompetenceO et Pronote.</p>}</section>}
    {rosters.length > 0 && <section className="pronote-rosters"><h2><Users size={20} /> Correspondances trouvées</h2>{rosters.map((roster) => <article key={roster.name}><strong>{roster.name}</strong><span>{roster.students.length} élèves récupérés</span><details><summary>Voir les élèves</summary><div>{roster.students.map((student) => <small key={student}>{student}</small>)}</div></details></article>)}</section>}
    {status?.lastError && !error && <p className="account-error">Dernière erreur : {status.lastError}</p>}
  </main>;
}
